import React, { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Sparkles, Loader2, Award, Clock, Download, Share2 } from "lucide-react";
import { api, formatApiError, API as API_BASE } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

/**
 * Module detail + reader. Left rail: lesson list with checkmarks. Right pane:
 * current lesson body with reflection questions + "Mark as complete" button.
 * On full completion, the certificate download button appears.
 */
export default function PlatformLearningModule() {
  const { user } = useAuth();
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [marking, setMarking] = useState(false);
  const lessonRef = useRef(null);

  const reload = async () => {
    try {
      const r = await api.get(`/learning/modules/${moduleId}`);
      setData(r.data);
      // Auto-pick first not-completed lesson
      if (!activeId) {
        const next = (r.data.lessons || []).find((l) => !l.completed) || r.data.lessons?.[0];
        if (next) setActiveId(next.id);
      }
    } catch (e) {
      toast.error("Module not found");
      navigate("/platform/learning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setActiveId(null);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  // Scroll to top of lesson on switch
  useEffect(() => {
    if (lessonRef.current) lessonRef.current.scrollTop = 0;
  }, [activeId]);

  const lesson = (data?.lessons || []).find((l) => l.id === activeId);

  const markComplete = async () => {
    if (!lesson) return;
    setMarking(true);
    try {
      await api.post(`/learning/modules/${moduleId}/lessons/${lesson.id}/complete`, { completed: true });
      toast.success("Lesson complete · well done");
      await reload();
      // Auto-advance to next not-yet-complete lesson
      const next = (data.lessons || []).find((l) => !l.completed && l.id !== lesson.id);
      if (next) setActiveId(next.id);
    } catch (e) {
      toast.error("Could not save progress");
    } finally {
      setMarking(false);
    }
  };

  const downloadCertificate = () => {
    // Open via fetch so cookies travel; download blob
    fetch(`${API_BASE}/learning/modules/${moduleId}/certificate`, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error("Could not generate");
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `ABGN-UK-Certificate-${data.title.replace(/[^a-z0-9]+/gi, "-")}.pdf`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        toast.success("Certificate downloaded");
      })
      .catch(() => toast.error("Could not download certificate"));
  };

  if (loading) return (
    <div className="bg-pearl h-screen flex items-center justify-center text-slate-500">
      <Loader2 size={20} className="animate-spin" />
    </div>
  );
  if (!data) return null;

  return (
    <div className="bg-pearl min-h-screen flex flex-col">
      <div className="px-4 sm:px-8 py-6 border-b border-navy/10 bg-white">
        <Link to="/platform/learning" className="inline-flex items-center gap-1.5 text-xs text-navy/60 hover:text-navy mb-3">
          <ArrowLeft size={12} /> Back to all modules
        </Link>
        <div className="flex items-baseline gap-3 flex-wrap">
          <div className="text-3xl">{data.cover_emoji}</div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl text-navy">{data.title}</h1>
            <p className="text-sm text-slate-600 italic">{data.subtitle}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 mb-1">
              <span>{data.progress.completed} of {data.progress.total} complete</span>
              <span className="text-saffron">{data.progress.percent}%</span>
            </div>
            <div className="h-1.5 bg-navy/10 rounded-sm overflow-hidden">
              <div className="h-full bg-saffron transition-all" style={{ width: `${data.progress.percent}%` }} />
            </div>
          </div>
          {data.progress.is_complete && (
            <>
              <button
                onClick={downloadCertificate}
                data-testid="learning-cert-download"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-saffron text-white rounded-sm text-sm font-medium hover:bg-saffron/90"
              >
                <Award size={14} /> Download certificate
              </button>
              <ShareCertificate moduleTitle={data.title} userName={user?.name || "ABGN-UK member"} />
            </>
          )}
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 max-w-7xl mx-auto w-full">
        {/* LESSON LIST */}
        <aside className="lg:col-span-4 xl:col-span-3 border-r border-navy/10 bg-white" data-testid="learning-lesson-list">
          <ul className="divide-y divide-navy/5">
            {data.lessons.map((ln, i) => (
              <li key={ln.id}>
                <button
                  onClick={() => setActiveId(ln.id)}
                  data-testid={`lesson-item-${ln.id}`}
                  className={`w-full text-left px-5 py-4 flex items-start gap-3 transition-colors ${
                    activeId === ln.id ? "bg-saffron/10" : "hover:bg-pearl"
                  }`}
                >
                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    ln.completed ? "bg-saffron text-white" : "bg-navy/10 text-navy"
                  }`}>
                    {ln.completed ? <Check size={14} /> : i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`font-medium text-sm ${activeId === ln.id ? "text-saffron" : "text-navy"}`}>
                      {ln.title}
                    </div>
                    <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-0.5">
                      <Clock size={10} /> {ln.minutes} min
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* LESSON BODY */}
        <main ref={lessonRef} className="lg:col-span-8 xl:col-span-9 overflow-y-auto px-5 sm:px-10 py-8 max-h-[calc(100vh-200px)]" data-testid="learning-lesson-body">
          {lesson ? (
            <article className="max-w-2xl mx-auto">
              <div className="text-[10px] uppercase tracking-[0.3em] text-saffron mb-2 inline-flex items-center gap-2">
                Lesson · <Clock size={10} /> {lesson.minutes} min
              </div>
              <h2 className="font-display text-3xl sm:text-4xl text-navy leading-tight">{lesson.title}</h2>
              <p className="mt-3 text-base sm:text-lg text-slate-700 italic leading-relaxed">{lesson.summary}</p>
              <div className="mt-7 prose prose-slate max-w-none whitespace-pre-line leading-[1.8] text-[16px] text-navy/90"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "18px" }}>
                {lesson.body_html}
              </div>
              {lesson.reflection && lesson.reflection.length > 0 && (
                <div className="mt-10 bg-white border border-saffron/30 rounded-sm p-6" data-testid="lesson-reflection">
                  <div className="inline-flex items-center gap-2 text-saffron mb-3">
                    <Sparkles size={16} />
                    <span className="text-[10px] uppercase tracking-[0.3em] font-semibold">Reflection</span>
                  </div>
                  <ol className="space-y-3 text-slate-700 leading-relaxed list-decimal pl-5">
                    {lesson.reflection.map((q, i) => <li key={i}>{q}</li>)}
                  </ol>
                </div>
              )}
              <div className="mt-10 flex items-center justify-between gap-3 flex-wrap pb-12">
                <button
                  onClick={markComplete}
                  disabled={marking || lesson.completed}
                  data-testid="lesson-mark-complete"
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm font-medium ${
                    lesson.completed
                      ? "bg-saffron/15 border border-saffron/40 text-saffron cursor-default"
                      : "bg-navy text-pearl hover:bg-navy-light"
                  } disabled:opacity-60`}
                >
                  {marking ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  {lesson.completed ? "Completed" : "Mark as complete"}
                </button>
                <NextLessonLink lessons={data.lessons} currentId={lesson.id} onPick={setActiveId} />
              </div>
            </article>
          ) : (
            <div className="text-center text-slate-500 py-20">Select a lesson to begin.</div>
          )}
        </main>
      </div>
    </div>
  );
}

function NextLessonLink({ lessons, currentId, onPick }) {
  const idx = lessons.findIndex((l) => l.id === currentId);
  const next = lessons[idx + 1];
  if (!next) return <span className="text-xs text-slate-400 italic">Last lesson</span>;
  return (
    <button
      onClick={() => onPick(next.id)}
      data-testid="lesson-next"
      className="inline-flex items-center gap-1.5 text-sm text-saffron hover:gap-2 transition-all"
    >
      Next: {next.title} <ArrowRight size={14} />
    </button>
  );
}

function ShareCertificate({ moduleTitle, userName }) {
  const [open, setOpen] = useState(false);
  const today = new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long" });
  const imgUrl = `${API_BASE}/share/certificate.png?name=${encodeURIComponent(userName)}&module=${encodeURIComponent(moduleTitle)}&date=${encodeURIComponent(today)}`;
  const shareText = `I just completed "${moduleTitle}" with the Ambedkarite Buddhist Global Network UK.`;
  const pageUrl = typeof window !== "undefined" ? `${window.location.origin}/platform/learning` : "";
  const enc = encodeURIComponent;

  const share = (target) => {
    const t = enc(shareText);
    const u = enc(pageUrl);
    let href = "";
    if (target === "whatsapp") href = `https://wa.me/?text=${t}%20${u}`;
    else if (target === "twitter") href = `https://twitter.com/intent/tweet?text=${t}&url=${u}`;
    else if (target === "linkedin") href = `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    else if (target === "facebook") href = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    if (href) window.open(href, "_blank", "noopener,noreferrer");
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(imgUrl);
      toast.success("Image link copied to clipboard");
    } catch (e) { /* ignore */ }
  };
  const downloadPng = async () => {
    try {
      const r = await fetch(imgUrl);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `abs-uk-certificate-${moduleTitle.replace(/\s+/g, "-").toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error("Could not download image");
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="learning-cert-share"
        className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-saffron text-saffron rounded-sm text-sm font-medium hover:bg-saffron/10"
      >
        <Share2 size={14} /> Share
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="share-cert-modal">
          <div className="bg-pearl rounded-xl shadow-2xl w-full max-w-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-navy/10 flex items-center justify-between">
              <div className="font-display text-lg text-navy">Share your achievement</div>
              <button onClick={() => setOpen(false)} className="text-navy/50 hover:text-navy text-sm">Close</button>
            </div>
            <div className="p-5 space-y-4">
              <img src={imgUrl} alt="Certificate preview" className="w-full rounded-md border border-navy/10 shadow-sm" data-testid="share-cert-preview" />
              <div className="flex flex-wrap gap-2 justify-center">
                <button onClick={() => share("whatsapp")} className="px-3.5 py-2 bg-emerald-600 text-white rounded-md text-sm hover:bg-emerald-700" data-testid="share-cert-whatsapp">WhatsApp</button>
                <button onClick={() => share("twitter")} className="px-3.5 py-2 bg-black text-white rounded-md text-sm hover:bg-neutral-800">X (Twitter)</button>
                <button onClick={() => share("linkedin")} className="px-3.5 py-2 bg-blue-700 text-white rounded-md text-sm hover:bg-blue-800">LinkedIn</button>
                <button onClick={() => share("facebook")} className="px-3.5 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">Facebook</button>
                <button onClick={copyLink} className="px-3.5 py-2 bg-white border border-navy/15 text-navy rounded-md text-sm hover:border-saffron">Copy link</button>
                <button onClick={downloadPng} className="px-3.5 py-2 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light">Download PNG</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
